/*
This file contains routes shared by both Backbone router and React router for transitionary purposes.
Once Backbone.Router is killed off, these methods can be refactored furthered into a more declarative
format.
*/

import $ from 'jquery';
import React, {Suspense, lazy} from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {
  Capability,
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  ProcessesFeature,
  RegistryFeature,
  ResultsFeature,
  UserPreferenceGatedCapability,
  V2RequestsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {makeMissingMessageForUserPrefGatedCapabilities} from 'auth/capabilities/CapabilityFeatureUserMessages';
import {
  warnAndRedirectAfterInaccessibleItemOpen,
  warnAndRedirectAfterInaccessiblePageOpen,
} from 'auth/capabilities/InaccessibleFeaturesActions';
import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {
  areAnyFeaturesEnabled,
  getFeatureStatus,
  isConnectFeatureEnabled,
} from 'auth/feature-enablement/FeatureStatus';
import DataPoliciesPage from 'auth/settings/DataPoliciesPage';
import DataPolicyPage from 'auth/settings/DataPolicyPage';
import SchemaPoliciesPage, {SchemaPoliciesPageTab} from 'auth/settings/SchemaPoliciesPage';
import SchemaPolicyPage from 'auth/settings/SchemaPolicyPage';
import BenchlingAppPage from 'benchling-apps/developer/BenchlingAppPage';
import BenchlingAppsPage from 'benchling-apps/developer/BenchlingAppsPage';
import CreateBenchlingAppPageLegacy from 'benchling-apps/developer/create-app/CreateBenchlingAppPageLegacy';
import InstallBenchlingAppPage from 'benchling-apps/installation/InstallBenchlingAppPage';
import AppCanvasBuilder from 'benchling-apps/interactivity/canvas-builder/AppCanvasBuilder';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {BULK_ANTIBODY_SCREEN} from 'bulk-workflows/antibody/BulkAntibodyConstants';
import {BULK_ASSEMBLY_SCREEN} from 'bulk-workflows/assembly/BulkAssemblyConstants';
import {BULK_TRANSLATION_SCREEN} from 'bulk-workflows/translation/BulkTranslationConstants';
import SaltLibrary from 'chemistry/salts/SaltLibrary';
import SolventLibrary from 'chemistry/solvents/SolventLibrary';
import {REQUESTS_V2_ANALYTICS_ACTIONS} from 'constants/analytics-products/RequestsV2AnalyticsConstants';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {RegistryPreferences} from 'constants/RegistryConstants';
import {GridSchemaClass} from 'constants/SampleConstants';
import type {UserReferralModalsResponse} from 'constants/UserConstants';
import * as UserDAO from 'dao/UserDAO';
import Editor from 'Editor';
import EventSubscriptionsPage from 'events/EventSubscriptionsPage';
import FileBrowserActions, {FileOpenParams} from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import CreateFromHashView from 'folder-items/CreateFromHashView';
import CreateFormDefinitionPage from 'forms/form-definition/CreateFormDefinitionPage';
import EditFormDefinitionPage from 'forms/form-definition/EditFormDefinitionPage';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {LegacyAssaySchemaType, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import {getDefaultConnectionSearchParams} from 'instruments/connections/connection-sidepanel/ConnectionSidepanel';
import InstrumentGatewaysPage from 'instruments/instrument-gateways/InstrumentGatewaysPage';
import InventorySettingsPage from 'inventory/InventorySettingsPage';
import RunSchemaEditor from 'lab-automation/settings/RunSchemaEditor';
import LabelPrintingSettingsPage from 'label-printing/LabelPrintingSettingsPage';
import {evaluateLDFlag} from 'launchdarkly/LaunchDarklyUtil';
import {MODEL_HUB_SCREEN} from 'model-hub/ModelHubConstants';
import {canRunScientificAIviaTAC} from 'model-hub/permissions/ModelHubPermissions';
import {areAaMonomersEnabledForUser} from 'molbio/aa-monomers/AaMonomersUtil';
import AaMonomerCreateFromCSVPage from 'molbio/aa-monomers/admin/AaMonomerCreateFromCSVPage';
import AaMonomerCreationMethodPicker from 'molbio/aa-monomers/admin/AaMonomerCreationMethodPicker';
import AaMonomerManualCreationPage from 'molbio/aa-monomers/admin/AaMonomerManualCreationPage';
import AaMonomerManualEditPage from 'molbio/aa-monomers/admin/AaMonomerManualEditPage';
import MonomerCreateFromCSVPage from 'molbio/monomers/admin/MonomerCreateFromCSVPage';
import MonomerCreationMethodPicker from 'molbio/monomers/admin/MonomerCreationMethodPicker';
import MonomerLibraryHELMPage from 'molbio/monomers/admin/MonomerLibraryHELMPage';
import MonomerManualCreationPage from 'molbio/monomers/admin/MonomerManualCreationPage';
import MonomerManualEditPage from 'molbio/monomers/admin/MonomerManualEditPage';
import {isMonomerManagementEnabledForUser} from 'molbio/monomers/admin/util';
import CustomSyntaxSection from 'molbio/monomers/custom-syntax/CustomSyntaxSection';
import IdtTableAndForm from 'molbio/monomers/custom-syntax/IdtTableAndForm';
import MonomerListing from 'molbio/monomers/MonomerListing';
import {areMonomersEnabledForUser} from 'molbio/monomers/MonomersUtil';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import EmptyPage from 'nav/home/EmptyPage';
import ShareMetadataModal, {SHARE_METADATA_MODAL_KEY} from 'nav/share/growth/ShareMetadataModal';
import {
  LegacyWorkflowQueryKey,
  parseAnalyticsURL,
  parseJellyfishURL,
  parseProjectsURL,
  parseSearchURL,
  parseStorablesURL,
  parseStudiesURL,
  parseTemplateCollectionURL,
  parseWorkflowsURL,
  parseWorklistItemsURL,
  parseWorklistsURL,
} from 'nav/sidepanel/listing/ParseURLs';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import TemplateCollectionPage from 'notebook/entry-templates/collections/TemplateCollectionPage';
import TemplateCollectionsPage from 'notebook/entry-templates/collections/TemplateCollectionsPage';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import getDefaultLibrarySearchParams from 'notebook/library/library-sidepanel/getDefaultLibrarySearchParams';
import NoteLinkedFilePreviewPage from 'notebook/notes/attachments/NoteLinkedFilePreviewPage';
import QueryingReviewLifecycleConfigurationEditor from 'notebook/reviews/review-lifecycles/ReviewLifecycleConfigurationEditor';
import QueryingReviewLifecyclesSettingsPage from 'notebook/reviews/review-lifecycles/ReviewLifecyclesListing';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import TFIActions from 'notebook/tfi/TFIActions';
import InviteUserModal from 'onboarding/InviteUserModal';
import {
  ROLE_INFO_SCREEN_WITHOUT_FLOW,
  SCREEN_TO_ANALYTICS_NAME,
  SIGNUP_FLOW_V2_AFFILIATION_SCREEN,
} from 'onboarding/new-user-flow/NewUserFlowConstants';
import * as RegistryActivationFlowActions from 'onboarding/registry-activation-flow/RegistryActivationFlowActions';
import {
  Organization,
  ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB,
  ORGANIZATION_SCREEN,
  TEAM_PAGE_SECTION_URL_PARAM_TO_TAB,
  TEAM_SCREEN,
  USER_PAGE_SECTION_URL_PARAM_TO_TAB,
  USER_SCREEN,
} from 'orgs/OrganizationConstants';
import PartyPageStore from 'orgs/PartyPageStore';
import DatadogUtil from 'performance/DatadogUtil';
import preferences from 'Preferences';
import ParameterLibraryPage from 'procedures/parameter-library/ui/ParameterLibraryPage';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import RequestV2DefinitionPage from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionPage';
import CreateProcessTaskSchemaPage from 'processes/settings/CreateProcessTaskSchemaPage';
import ProcessTaskSchemasPage from 'processes/settings/ProcessTaskSchemasPage';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {getStore} from 'redux-stores/Store';
import RegistryActions from 'registry/RegistryActions';
import CreateRequestTaskSchemaPage from 'requests/schemas/CreateRequestTaskSchemaPage';
import {REQUEST_SCHEMA_SIDEBAR_SECTIONS} from 'requests/schemas/RequestSchemaConstants';
import RequestTaskSchemaPage from 'requests/schemas/RequestTaskSchemaPage';
import RequestTaskSchemasPage from 'requests/schemas/RequestTaskSchemasPage';
import {ASSAY_SCHEMA_SIDEBAR_SECTIONS, ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import AssaySchemasPage from 'results/assay-schemas/AssaySchemasPage';
import CreateAssaySchemaPage from 'results/assay-schemas/CreateAssaySchemaPage';
import ResultSchemaEditor from 'results/assay-schemas/ResultSchemaEditor';
import SchemaPage from 'results/assay-schemas/SchemaPage';
import TestDefinitionEditor from 'sample-tests/ui/test-definitions/TestDefinitionEditor';
import TestDefinitionsPage from 'sample-tests/ui/test-definitions/TestDefinitionsPage';
import SampleTypesListing from 'sample-types/SampleTypesListing';
import * as StorableActions from 'samples/StorableActions';
import SchemaInterfaceEditor from 'schemas/schema-interface/SchemaInterfaceEditor';
import SchemaInterfacesPage from 'schemas/schema-interface/SchemaInterfacesPage';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import * as FilterUtil from 'search/FilterUtil';
import {SearchParams} from 'search/SearchConstants';
import {SEARCH_TYPE_BY_SECTION} from 'search/SearchTypes';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import LabelPrintingSettingsContainer from 'settings/label-printing/LabelPrintingSettingsContainer';
import LabelTemplateSettingsPage from 'settings/label-printing/LabelTemplateSettingsPage';
import PrinterSettingsPage from 'settings/label-printing/PrinterSettingsPage';
import RegistryGeneralSettingsPage from 'settings/registry/RegistryGeneralSettingsPage';
import RegistrySelectorsSettingsPage from 'settings/registry/RegistrySelectorsSettingsPage';
import RegistryTagSchemasSettingsPage from 'settings/registry/RegistryTagSchemasSettingsPage';
import SchemaFieldSelectorSettingsPage from 'settings/registry/SchemaFieldSelectorSettingsPage';
import TagSchemaCreateFlow from 'settings/registry/TagSchemaCreateFlow';
import TagSchemaSettingsPage, {settingsTabForRouteSection} from 'settings/registry/TagSchemaSettingsPage';
import ContainerSchemasPage from 'settings/samples/ContainerSchemasPage';
import GridSchemaSettingsPage from 'settings/samples/GridSchemaSettingsPage';
import GridSchemasPage from 'settings/samples/GridSchemasPage';
import LocationSchemaSettingsPage, {
  locationSettingsTabForRouteSection,
} from 'settings/samples/LocationSchemaSettingsPage';
import LocationSchemasPage from 'settings/samples/LocationSchemasPage';
import SidebarActions from 'sidebar/SidebarActions';
import * as SidebarConstants from 'sidebar/SidebarConstants';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';
import StudySchemaEditor from 'studies/study-schemas/StudySchemaEditor';
import StudySchemasPage from 'studies/study-schemas/StudySchemasPage';
import {
  TenantAdminTab,
  TENANT_ADMIN_CUSTOMIZE_SUB_SECTION_TO_SETTINGS_HASH,
  TENANT_ADMIN_PAGE_SECTION_URL_PARAM_TO_TAB,
} from 'tenants/admin/TenantAdminConstants';
import * as TenantAdminReduxActions from 'tenants/admin/TenantAdminReduxActions';
import * as TenantActions from 'tenants/TenantActions';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {CenteredSpinner} from 'ui-library/Spinner';
import UnitDictionaryListing from 'units/UnitDictionaryListing';
import APIUtil from 'util/APIUtil';
import {formatBytes} from 'util/NameUtil';
import * as WorkflowActions from 'workflows/WorkflowActions';
import * as WorkflowTemplateActions from 'workflows/WorkflowTemplateActions';
import {getDefaultWorklistSearchParams} from 'worklists/WorklistSearchParams';
import {updateSidepanelSelectedWorklist} from 'worklists/WorklistUIActions';

let editorView: Editor;

if (module.hot && module.hot.data) {
  // get preserved editor view from before HMR reload
  editorView = module.hot.data.editorView;
}

function _getDisabledFeatureUserMessage(features: Array<FeatureDefinition>): string | null {
  const disabledFeatureStatus = features
    .map((feature) => getFeatureStatus(feature))
    .find((featureStatus) => !featureStatus.enabled);
  if (disabledFeatureStatus && !disabledFeatureStatus.enabled) {
    return disabledFeatureStatus.userMessage ?? null;
  }
  return null;
}

function _redirectForWorkspace(): void {
  const fileStates = FileStates.get();
  const {activeId, workspaceItems} = WorkspaceStore.getState();
  if (activeId) {
    fileStates.openFolderItem(activeId);
  } else if (workspaceItems.length > 0) {
    fileStates.openFolderItem(workspaceItems[0].fileId);
  } else {
    fileStates.trigger('no-files');
  }
}

// Workflows and requests are gated for capability management via legacy config flags, so we check them here
// individually. The gate for all other items is in FileBrowserActions.openFile.
function _redirectAfterInaccessibleLegacyGatedOpen(
  capability: UserPreferenceGatedCapability,
  itemId?: string
): void {
  const userMessage = makeMissingMessageForUserPrefGatedCapabilities([capability]);
  if (itemId) {
    warnAndRedirectAfterInaccessibleItemOpen(itemId, userMessage);
  } else {
    warnAndRedirectAfterInaccessiblePageOpen(userMessage);
  }

  _editorBase();
}

export function _editorBase(callback?: () => void): void {
  // Reset the FullScreenStore to a state where it is not showing a screen
  FullScreenActions.hideScreen();

  if (editorView) {
    if (callback) {
      // Always close subcomponents before rending
      editorView.closeSubcomponents();
      callback();
    }
    return;
  }

  WorkspaceStore.loadFromServerBootstrap();

  renderToDOM(<EmptyPage />, $('.emptyPage-reactMount')[0]);
  import(
    /* webpackChunkName: "editor", webpackPrefetch: true */
    '../Editor'
  ).then(({default: Editor}) => {
    editorView = new Editor();
    editorView.render();
    // FileBarView (an Editor subcomponent) listens to the "open" event on a fileState to coordinate all
    // subcomponents to render the fileState; however, this listener is added during Editor.initialize,
    // which may be called /after/ the fileState is fetched (i.e. on initial pageload). To handle this case
    // where there's already an active fileState, we trigger the "open" event again now that the Editor is
    // rendered and ready to handle the fileState.
    const activeFileState = FileStates.get().findWhere({active: true});
    if (activeFileState) {
      activeFileState.trigger('open', activeFileState);
    }

    new CreateFromHashView().render();

    if (window.location.hash === '#tfi') {
      TFIActions.toggleTFIAddModal(true);
    }

    if (EntityStore.currentUser() != null) {
      UserDAO.getReferralModals().done(({showInviteModal}: UserReferralModalsResponse) => {
        if (!showInviteModal) {
          return;
        }

        LegacyModalContainer.open(
          InviteUserModal,
          <InviteUserModal
            onRequestClose={() => {
              LegacyModalContainer.close(InviteUserModal);
            }}
            copy={
              <div>
                <p>
                  Users get the most value out of Benchling when they share notebooks, sequences, oligos, PDFs
                  and other data with colleagues. Invite other scientists who would benefit from using
                  Benchling!
                </p>
                <p>
                  {'P.S. Get '}
                  <strong>
                    {`${formatBytes(OnboardingConstants.SPACE_ADDITION_SIZES.INVITE_MEMBER)} of
                       free space for every person you invite and access to`}
                  </strong>{' '}
                  <a href="/invite" target="_blank" rel="noreferrer noopener">
                    Benchling Gold
                  </a>{' '}
                  after 10.
                </p>
              </div>
            }
          />
        );
      });
    }
    if (callback) {
      callback();
    }
  });
}

function _sidepanelListing(section: NavConstants.Section, searchParams: SearchParams): void {
  getStore().dispatch(
    SearchUIReduxActions.updateParamsAndSearch(
      section,
      SEARCH_TYPE_BY_SECTION[section],
      searchParams,
      // Assume that invalid filters are removed before params are URL-serialized
      getEmptyFilterErrors(searchParams.filters)
    )
  );
  _editorBase(() => {
    SidepanelActions.expandSection(section);
  });
}

function _openWorkflowScreen(screenAction: () => void): void {
  _editorBase(() => {
    screenAction();
    SidepanelActions.expandSection(NavConstants.SECTIONS.WORKFLOWS);
  });
}

function _editorForApiId(fileId: string, queryKey?: FileOpenParams, callback?: () => void): void {
  // Fetching a file will properly load it into the store, which is notably not dependent on the editor
  // view being loaded. By calling openItem before _editorBase, we get better performance as the network
  // request happens in parallel to the editor initialization and rendering.
  if (fileId) {
    // Before fetching file, load workspace from server bootstrap so that the file is not prematurely added
    // to the workspace with a different GUID (T12621)
    WorkspaceStore.loadFromServerBootstrap();
    FileBrowserActions.openItem(fileId, queryKey);
  }
  _editorBase(callback);
}

const EditorRoutes = {
  hotReload(): void {
    if (editorView) {
      editorView.buffersView.bufferViews.forEach((bufferView) => {
        bufferView.renderTabIfActive(bufferView.renderedTab ?? undefined, true);
      });
    }
  },

  createOrganizationPage(): void {
    DatadogUtil.startRUMView('organization_create');
    _editorBase(() => {
      OrganizationActions.openCreateOrganizationPage({name: ''});
    });
  },

  organizationPage(organizationUrlIdentifier: string, section?: string): void {
    DatadogUtil.startRUMView('organization_edit');
    _editorBase(() => {
      const currentOrganization = PartyPageStore.getCurrentOrganization();
      const currentScreen = FullScreenStore.getCurrentScreen();
      if (
        currentScreen === ORGANIZATION_SCREEN &&
        currentOrganization &&
        (currentOrganization as Organization).url_identifier === organizationUrlIdentifier
      ) {
        OrganizationActions.updateActiveTab(
          (currentOrganization as Organization).id,
          ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB[
            section as keyof typeof ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB
          ]
        );
        return;
      }
      OrganizationActions.openOrganizationPage(organizationUrlIdentifier, section!);
    });
  },

  userPage(userId: string, section: string | null = null): void {
    DatadogUtil.startRUMView('user_edit');
    _editorBase(() => {
      const currentScreen = FullScreenStore.getCurrentScreen();
      // @ts-expect-error if we _were_ on the create organization page we may not have a full entity. in this
      // case currentEntityId will end up undefined, which is fine since we're just using it to compare
      const currentEntityId = PartyPageStore.getCurrentEntity()?.id;
      if (currentScreen === USER_SCREEN && currentEntityId === userId) {
        OrganizationActions.updateActiveTab(
          userId,
          USER_PAGE_SECTION_URL_PARAM_TO_TAB[section as keyof typeof USER_PAGE_SECTION_URL_PARAM_TO_TAB]
        );
        return;
      }
      OrganizationActions.openUserPage(userId, section);
    });
  },

  dataPoliciesPage(): void {
    DatadogUtil.startRUMView('data_policies_list');
    _editorBase(() => {
      editorView.renderReact(<DataPoliciesPage />, SidebarConstants.Section.DATA_POLICIES);
    });
  },

  dataPolicyPage(dataPolicyId: string): void {
    DatadogUtil.startRUMView('data_policy_edit');
    _editorBase(() => {
      editorView.renderReact(
        <DataPolicyPage dataPolicyId={dataPolicyId} />,
        SidebarConstants.Section.DATA_POLICIES
      );
    });
  },

  schemaPoliciesPage(): void {
    DatadogUtil.startRUMView('schema_policies_list');
    const tab = SchemaPoliciesPageTab.POLICIES_TAB;
    _editorBase(() => {
      editorView.renderReact(
        <SchemaPoliciesPage activeTab={tab} />,
        SidebarConstants.Section.SCHEMA_POLICIES
      );
    });
  },

  schemaPoliciesPageSettingsTab(): void {
    const tab = SchemaPoliciesPageTab.SETTINGS_TAB;
    _editorBase(() => {
      editorView.renderReact(
        <SchemaPoliciesPage activeTab={tab} />,
        SidebarConstants.Section.SCHEMA_POLICIES
      );
    });
  },

  schemaPolicyPage(schemaPolicyId: string): void {
    DatadogUtil.startRUMView('schema_policy_edit');
    _editorBase(() => {
      editorView.renderReact(
        <SchemaPolicyPage schemaPolicyId={schemaPolicyId} />,
        SidebarConstants.Section.SCHEMA_POLICIES
      );
    });
  },

  datasetEditor(datasetId: string): void {
    if (!BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS) {
      warnAndRedirectAfterInaccessiblePageOpen('Datasets are not enabled');
      return;
    }
    _editorForApiId(datasetId);
  },

  formDefinitionCreate(userId: string): void {
    if (!isWorkflowCreateTaskFormsEnabled()) {
      warnAndRedirectAfterInaccessiblePageOpen('Forms are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('form_definition_create');

    const collectionId = benchlingUtil.parseUri(window.location.href).queryKey.templateCollection;

    _editorBase(() => {
      TemplateActions.activateCollection(collectionId);
      editorView.renderReact(
        <CreateFormDefinitionPage />,
        SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
      );
    });
  },

  formDefinitionEditor(userId: string, formDefinitionId: string): void {
    if (!isWorkflowCreateTaskFormsEnabled()) {
      warnAndRedirectAfterInaccessiblePageOpen('Forms are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('form_definition_editor');

    if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      _editorForApiId(formDefinitionId);
    } else {
      _editorBase(() => {
        editorView.renderReact(
          <EditFormDefinitionPage formDefinitionId={formDefinitionId} />,
          SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
        );
      });
    }
  },

  homepage(): void {
    if (!BenchlingConfig.NEW_HOMEPAGE_LA_OPT_IN && !evaluateLDFlag('NAV_ENABLE_HOMEPAGE_REDESIGN')) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('homepage');
    _editorBase(() => {
      SidepanelActions.expandSection(NavConstants.SECTIONS.HOME);
    });
  },

  monomers(): void {
    const featureStatus = getFeatureStatus({feature: MolbioFeature.MOLBIO_OPEN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    if (!areMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomers_list');
    _editorBase(() => {
      editorView.renderReact(<MonomerListing />, SidebarConstants.Section.MONOMERS);
    });
  },

  aaMonomerList(): void {
    const featureStatus = getFeatureStatus({feature: MolbioFeature.MOLBIO_OPEN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    if (!BenchlingConfig.MOLBIO_ENABLE_AA_MONOMER_LIBRARY) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomers_list');
    const AaMonomerListing = lazy(
      () => import(/* webpackChunkName: "aa-monomer-listing" */ 'molbio/aa-monomers/AaMonomerListing')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <AaMonomerListing />
        </Suspense>,
        SidebarConstants.Section.AA_MONOMERS
      );
    });
  },

  aaMonomerCreateFromHELMLibraryPage(): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_create_helm_library');
    const AaMonomerHELMPage = lazy(
      () =>
        import(/* webpackChunkName: "aa-monomer-helm-page" */ 'molbio/aa-monomers/admin/AaMonomerHELMPage')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredSpinner />}>
          <AaMonomerHELMPage />
        </Suspense>,
        SidebarConstants.Section.AA_MONOMERS
      );
    });
  },

  aaMonomerViewHELMLibraryPage(): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_view_helm_library');
    const AaMonomerHELMPage = lazy(
      () =>
        import(/* webpackChunkName: "aa-monomer-helm-page" */ 'molbio/aa-monomers/admin/AaMonomerHELMPage')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredSpinner />}>
          <AaMonomerHELMPage viewOnly={true} />
        </Suspense>,
        SidebarConstants.Section.AA_MONOMERS
      );
    });
  },

  aaMonomerCreateMethodPicker(): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_create');
    _editorBase(() => {
      editorView.renderReact(<AaMonomerCreationMethodPicker />, SidebarConstants.Section.AA_MONOMERS);
    });
  },

  aaMonomerManualCreationPage(): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_create_manual');
    _editorBase(() => {
      editorView.renderReact(<AaMonomerManualCreationPage />, SidebarConstants.Section.AA_MONOMERS);
    });
  },

  aaMonomerCreateFromCSVPage(): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_create_from_csv');
    _editorBase(() => {
      editorView.renderReact(<AaMonomerCreateFromCSVPage />, SidebarConstants.Section.AA_MONOMERS);
    });
  },

  aaMonomerManualEditPage(aaMonomerId: string): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_edit');
    _editorBase(() => {
      editorView.renderReact(
        <AaMonomerManualEditPage aaMonomerId={aaMonomerId} />,
        SidebarConstants.Section.AA_MONOMERS
      );
    });
  },

  aaMonomerReadOnlyPage(aaMonomerId: string): void {
    if (!areAaMonomersEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('aa_monomer_view');
    _editorBase(() => {
      editorView.renderReact(
        <AaMonomerManualEditPage aaMonomerId={aaMonomerId} viewOnly={true} />,
        SidebarConstants.Section.AA_MONOMERS
      );
    });
  },

  monomerCreateMethodPicker(): void {
    if (!isMonomerManagementEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_create');
    _editorBase(() => {
      editorView.renderReact(<MonomerCreationMethodPicker />, SidebarConstants.Section.MONOMERS);
    });
  },

  monomerCreateFromHELMLibraryPage(): void {
    if (!isMonomerManagementEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_create_helm_library');
    _editorBase(() => {
      editorView.renderReact(<MonomerLibraryHELMPage />, SidebarConstants.Section.MONOMERS);
    });
  },

  monomerViewHELMLibraryPage(): void {
    DatadogUtil.startRUMView('monomer_view_helm_library');
    _editorBase(() => {
      editorView.renderReact(<MonomerLibraryHELMPage viewOnly={true} />, SidebarConstants.Section.MONOMERS);
    });
  },

  monomerManualCreationPage(): void {
    if (!isMonomerManagementEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_create_manual');
    _editorBase(() => {
      editorView.renderReact(<MonomerManualCreationPage />, SidebarConstants.Section.MONOMERS);
    });
  },

  monomerManualEditPage(monomerId: string): void {
    if (!isMonomerManagementEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_edit_manual');
    _editorBase(() => {
      editorView.renderReact(
        <MonomerManualEditPage monomerId={monomerId} />,
        SidebarConstants.Section.MONOMERS
      );
    });
  },

  monomerCreateFromCSVPage(): void {
    if (!isMonomerManagementEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_create_from_csv');
    _editorBase(() => {
      editorView.renderReact(<MonomerCreateFromCSVPage />, SidebarConstants.Section.MONOMERS);
    });
  },

  monomerCustomNotationsPage(vendorSyntaxId?: string): void {
    if (!isMonomerManagementEnabledForUser() || !BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_custom_notations');
    _editorBase(() => {
      editorView.renderReact(
        <CustomSyntaxSection initialVendorSyntaxId={vendorSyntaxId} />,
        SidebarConstants.Section.MONOMERS
      );
    });
  },

  monomerIdtAliasesPage(): void {
    if (!isMonomerManagementEnabledForUser() || !BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('monomer_idt_aliases');
    _editorBase(() => {
      editorView.renderReact(<IdtTableAndForm />, SidebarConstants.Section.MONOMERS);
    });
  },

  saltLibrary(): void {
    DatadogUtil.startRUMView('salt_library');
    _editorBase(() => {
      editorView.renderReact(<SaltLibrary />, SidebarConstants.Section.SALT_LIBRARY);
    });
  },

  solventLibrary(): void {
    DatadogUtil.startRUMView('solvent_library');
    _editorBase(() => {
      editorView.renderReact(<SolventLibrary />, SidebarConstants.Section.SOLVENT_LIBRARY);
    });
  },

  unitsDictionaryListing(): void {
    DatadogUtil.startRUMView('unit_dictionary_settings');
    _editorBase(() => {
      editorView.renderReact(<UnitDictionaryListing />, SidebarConstants.Section.UNIT_DICTIONARY);
    });
  },

  reviewLifecyclesListing(): void {
    if (!areReviewLifecyclesEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }
    DatadogUtil.startRUMView('review_lifecycles_list');
    _editorBase(() => {
      editorView.renderReact(
        <QueryingReviewLifecyclesSettingsPage />,
        SidebarConstants.Section.REVIEW_LIFECYCLES
      );
    });
  },

  reviewLifecycleConfiguration(reviewLifecycleId?: string): void {
    if (!areReviewLifecyclesEnabledForUser()) {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
      return;
    }

    DatadogUtil.startRUMView('configure_review_lifecycle');
    _editorBase(() => {
      editorView.renderReact(
        <QueryingReviewLifecycleConfigurationEditor reviewLifecycleId={reviewLifecycleId} />,
        SidebarConstants.Section.REVIEW_LIFECYCLES
      );
    });
  },

  createTeamPage(organizationId: string): void {
    DatadogUtil.startRUMView('team_create');
    _editorBase(() => {
      OrganizationActions.openCreateTeamPage(organizationId);
    });
  },

  teamPage(teamId: string, section: keyof typeof TEAM_PAGE_SECTION_URL_PARAM_TO_TAB): void {
    DatadogUtil.startRUMView(`team_edit`); // TODO: team section changes do not go through router
    _editorBase(() => {
      const currentScreen = FullScreenStore.getCurrentScreen();
      // @ts-expect-error if we _were_ on the create organization page we may not have a full entity. in this
      // case currentEntityId will end up undefined, which is fine since we're just using it to compare
      const currentEntityId = PartyPageStore.getCurrentEntity()?.id;
      if (currentScreen === TEAM_SCREEN && currentEntityId === teamId) {
        OrganizationActions.updateActiveTab(teamId, TEAM_PAGE_SECTION_URL_PARAM_TO_TAB[section]);
        return;
      }
      OrganizationActions.openTeamPage(teamId, section);
    });
  },

  tenantAdmin(section: keyof typeof TENANT_ADMIN_PAGE_SECTION_URL_PARAM_TO_TAB, subSection?: string): void {
    const tab = TENANT_ADMIN_PAGE_SECTION_URL_PARAM_TO_TAB[section];

    // The Customize tab was removed; redirect bookmarked /admin/customize/* URLs to Settings.
    if (tab === TenantAdminTab.EXTENSIONS_TAB) {
      const hash =
        subSection != null ? TENANT_ADMIN_CUSTOMIZE_SUB_SECTION_TO_SETTINGS_HASH[subSection] : 'agent-skills';
      window.location.replace(`/admin/settings#${hash ?? 'agent-skills'}`);
      return;
    }

    DatadogUtil.startRUMView(tab != null ? `tenant_${section}_edit` : 'tenant_edit');
    getStore().dispatch(TenantAdminReduxActions.changeActiveTab(tab));

    _editorBase(() => {
      TenantActions.openTenantAdminPage();
    });
  },

  orgModal(): void {
    DatadogUtil.startRUMView('org_modal');
    _editorBase(() => {
      HomeDashboardActions.openSearchOrgModal();
    });
  },

  gettingStartedFlow(): void {
    DatadogUtil.startRUMView('getting_started_flow');
    _editorBase(() => {
      const initialScreen = SIGNUP_FLOW_V2_AFFILIATION_SCREEN;
      if (Object.keys(SCREEN_TO_ANALYTICS_NAME).includes(initialScreen)) {
        analytics.track('onboarding_page', 'viewed', {
          page: SCREEN_TO_ANALYTICS_NAME[initialScreen],
          eventLastModified: 'FY22_Q2b3',
        });
      }
      FullScreenActions.showScreen(initialScreen);
    });
  },

  registryActivationFlow(): void {
    DatadogUtil.startRUMView('registry_activation_flow');
    _editorBase(() => {
      // Note that ENABLE_REGISTRY_ACTIVATION can no longer be set through the application. This flow remains
      // for ad-hoc use.
      const registryActivationEnabled = preferences.get(RegistryPreferences.ENABLE_REGISTRY_ACTIVATION);
      if (registryActivationEnabled) {
        RegistryActivationFlowActions.showStartingScreen('router');
      } else {
        SidepanelActions.expandSection(NavConstants.SECTIONS.HOME);
      }
    });
  },

  roleInfoScreen(): void {
    DatadogUtil.startRUMView('role_info');
    _editorBase(() => {
      FullScreenActions.showScreen(ROLE_INFO_SCREEN_WITHOUT_FLOW);
    });
  },

  elnFolder(userId: string, unprefixedFolderId: string, queryString?: string): void {
    const apiPrefix = APIUtil.typeToApiPrefix(ModelTypeConstants.FOLDER);
    const apiIdentifier = `${apiPrefix}_${unprefixedFolderId.split('-')[0]}`;
    const {queryKey} = benchlingUtil.parseUri(window.location.href);

    DatadogUtil.startRUMView('eln_folder');
    _editorBase(() => {
      const baseURL = window.location.pathname;
      require('FileStates').default.get().openFolder(apiIdentifier, baseURL, queryKey);
    });
  },

  calendar(): void {
    DatadogUtil.startRUMView('calendar');
    _editorBase(() => {
      SidepanelActions.expandSection(NavConstants.SECTIONS.HOME);
    });
  },

  projects(): void {
    DatadogUtil.startRUMView('projects');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseProjectsURL(queryKey);
    if (!searchParams) {
      _editorBase(() => {
        SidepanelActions.expandSection(NavConstants.SECTIONS.PROJECTS);
      });
    } else {
      _sidepanelListing(NavConstants.Section.PROJECTS, searchParams);
    }
  },

  registries(): void {
    DatadogUtil.startRUMView('registries');
    _editorBase(() => {
      SidepanelActions.expandSection(NavConstants.SECTIONS.REGISTRIES);
    });
  },

  worklists(): void {
    DatadogUtil.startRUMView('worklists');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseWorklistsURL(queryKey);
    _sidepanelListing(NavConstants.Section.WORKLISTS, searchParams);
  },

  worklistItems(): void {
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const {searchParams, worklistId} = parseWorklistItemsURL(queryKey);
    if (!worklistId) {
      DatadogUtil.startRUMView('worklists');
      // Redirect to worklist listing with default search params
      _sidepanelListing(NavConstants.Section.WORKLISTS, getDefaultWorklistSearchParams());
    } else {
      DatadogUtil.startRUMView('worklist_items');
      getStore().dispatch(updateSidepanelSelectedWorklist(worklistId));
      _sidepanelListing(NavConstants.Section.WORKLIST_ITEMS, searchParams);
    }
  },

  // TODO: Move jellyfish() to client/app-core/archetype/routes/JellyfishRoutes.ts
  jellyfish(): void {
    if (!BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE) {
      warnAndRedirectAfterInaccessiblePageOpen('You do not have access to this feature.');
      return;
    }

    DatadogUtil.startRUMView('jellyfish');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseJellyfishURL(queryKey);
    _sidepanelListing(NavConstants.Section.JELLYFISH, searchParams);
  },

  jellyfishEditor(jellyfishId: string): void {
    if (!BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE) {
      warnAndRedirectAfterInaccessiblePageOpen('You do not have access to this feature.');
      return;
    }

    _editorForApiId(jellyfishId);
  },

  worksheetEditor(worksheetId: string): void {
    // TODO (BNCH-103204): Replace with capability management check for correct feature based on procedure type
    if (!BenchlingConfig.PROCEDURES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('You do not have access to this feature.');
      return;
    }

    _editorForApiId(worksheetId);
  },

  search(): void {
    DatadogUtil.startRUMView('search');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseSearchURL(queryKey)!;
    _sidepanelListing(NavConstants.Section.SEARCH, searchParams);
  },

  modelHub(): void {
    if (!canRunScientificAIviaTAC()) {
      warnAndRedirectAfterInaccessiblePageOpen();
      return;
    }
    DatadogUtil.startRUMView('model-hub');
    _editorBase(() => {
      FullScreenActions.showScreen(MODEL_HUB_SCREEN);
    });
  },

  instrumentGateways(): void {
    if (!isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})) {
      warnAndRedirectAfterInaccessiblePageOpen('Instruments are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('instrument_gateways_list');
    _editorBase(() => {
      editorView.renderReact(<InstrumentGatewaysPage />, SidebarConstants.Section.INSTRUMENT_GATEWAYS);
    });
  },

  connectionListing(): void {
    this.sidepanelListing(NavConstants.SECTIONS.CONNECTIONS, getDefaultConnectionSearchParams());
  },

  connectionEditor(connectionId: string): void {
    if (!isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_OPEN})) {
      warnAndRedirectAfterInaccessiblePageOpen('Instruments are not enabled.');
      return;
    }
    DatadogUtil.startRUMView('connection_editor');
    _editorForApiId(connectionId);
  },

  processTaskSchemas(): void {
    const featureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('process_task_schemas_list');
    _editorBase(() => {
      editorView.renderReact(<ProcessTaskSchemasPage />, SidebarConstants.Section.PROCESS_SCHEMAS);
    });
  },

  processTaskSchemasCreate(): void {
    const featureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('process_task_schemas_create');
    _editorBase(() => {
      editorView.renderReact(<CreateProcessTaskSchemaPage />, SidebarConstants.Section.PROCESS_SCHEMAS);
    });
  },

  processTaskSchema(schemaURLIdentifier: string): void {
    const featureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('process_task_schema_edit');
    _editorBase(() => {
      const ProcessTaskSchemaEditor = lazy(
        () =>
          import(
            /* webpackChunkName: "process-task-schema-editor" */ 'processes/settings/ProcessTaskSchemaEditor'
          )
      );
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <ProcessTaskSchemaEditor schemaId={schemaURLIdentifier} key={schemaURLIdentifier} />
        </Suspense>,
        SidebarConstants.Section.PROCESS_SCHEMAS
      );
    });
  },

  requestSchemas(): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_schemas_list');
    _editorBase(() => {
      editorView.renderReact(
        <AssaySchemasPage schemaType={SCHEMA_TYPES.ASSAY_REQUEST} />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.ASSAY_REQUEST]
      );
    });
  },

  requestSchemasCreate(): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_schemas_create');
    _editorBase(() => {
      editorView.renderReact(
        <CreateAssaySchemaPage schemaType={SCHEMA_TYPES.ASSAY_REQUEST} />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.ASSAY_REQUEST]
      );
    });
  },

  requestSchema(ownerURL: string, schemaURLIdentifier: string): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_schema_edit');
    _editorBase(() => {
      editorView.renderReact(
        <SchemaPage schemaId={schemaURLIdentifier} schemaType={SCHEMA_TYPES.ASSAY_REQUEST} />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.ASSAY_REQUEST]
      );
    });
  },

  requestTaskSchemas(): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_task_schemas_list');
    _editorBase(() => {
      editorView.renderReact(
        <RequestTaskSchemasPage />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.REQUEST_TASK]
      );
    });
  },

  requestTaskSchemaCreate(): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_task_schema_create');
    _editorBase(() => {
      editorView.renderReact(
        <CreateRequestTaskSchemaPage />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.REQUEST_TASK]
      );
    });
  },

  requestTaskSchema(ownerURL: string, schemaURLIdentifier: string): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      warnAndRedirectAfterInaccessiblePageOpen(
        makeMissingMessageForUserPrefGatedCapabilities([Capability.REQUESTS])
      );
      return;
    }

    DatadogUtil.startRUMView('request_task_schema_edit');
    _editorBase(() => {
      editorView.renderReact(
        <RequestTaskSchemaPage schemaId={schemaURLIdentifier} schemaType={SCHEMA_TYPES.REQUEST_TASK} />,
        REQUEST_SCHEMA_SIDEBAR_SECTIONS[SCHEMA_TYPES.REQUEST_TASK]
      );
    });
  },

  assaySchemas(schemaType: LegacyAssaySchemaType): void {
    assert(schemaType !== ASSAY_TYPES.ASSAY_REQUEST, 'Use EditorRoutes.requestSchemas() for Requests');
    const features =
      schemaType === ASSAY_TYPES.ASSAY_RUN
        ? [{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]
        : [{feature: ResultsFeature.RESULTS_ADMIN}];
    if (!areAnyFeaturesEnabled(features)) {
      warnAndRedirectAfterInaccessiblePageOpen(_getDisabledFeatureUserMessage(features)!);
      return;
    }

    DatadogUtil.startRUMView('assay_schemas_list');
    _editorBase(() => {
      editorView.renderReact(
        <AssaySchemasPage schemaType={schemaType} />,
        ASSAY_SCHEMA_SIDEBAR_SECTIONS[schemaType]
      );
    });
  },

  assaySchemasCreate(schemaType: LegacyAssaySchemaType): void {
    assert(schemaType !== ASSAY_TYPES.ASSAY_REQUEST, 'Use EditorRoutes.requestSchemasCreate() for Requests');
    const features =
      schemaType === ASSAY_TYPES.ASSAY_RUN
        ? [{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]
        : [{feature: ResultsFeature.RESULTS_ADMIN}];
    if (!areAnyFeaturesEnabled(features)) {
      warnAndRedirectAfterInaccessiblePageOpen(_getDisabledFeatureUserMessage(features)!);
      return;
    }

    DatadogUtil.startRUMView('assay_schema_create');
    _editorBase(() => {
      editorView.renderReact(
        <CreateAssaySchemaPage schemaType={schemaType} />,
        ASSAY_SCHEMA_SIDEBAR_SECTIONS[schemaType]
      );
    });
  },

  assaySchema(schemaType: LegacyAssaySchemaType, ownerURL: string, assaySchemaURLIdentifier: string): void {
    assert(schemaType !== ASSAY_TYPES.ASSAY_REQUEST, 'Use EditorRoutes.requestSchema() for Requests');
    const features =
      schemaType === ASSAY_TYPES.ASSAY_RUN
        ? [{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]
        : [{feature: ResultsFeature.RESULTS_ADMIN}];
    if (!areAnyFeaturesEnabled(features)) {
      warnAndRedirectAfterInaccessiblePageOpen(_getDisabledFeatureUserMessage(features)!);
      return;
    }
    DatadogUtil.startRUMView('assay_schema_edit');
    _editorBase(() => {
      editorView.renderReact(
        <SchemaPage schemaId={assaySchemaURLIdentifier} schemaType={schemaType} />,
        ASSAY_SCHEMA_SIDEBAR_SECTIONS[schemaType]
      );
    });
  },

  resultSchemaCreate(): void {
    DatadogUtil.startRUMView('result_schema_create');
    _editorBase(() => {
      editorView.renderReact(<ResultSchemaEditor />, ASSAY_SCHEMA_SIDEBAR_SECTIONS[ASSAY_TYPES.ASSAY_RESULT]);
    });
  },

  resultSchema(ownerURL: string, resultSchemaURLIdentifier: string): void {
    DatadogUtil.startRUMView('result_schema_edit');
    _editorBase(() => {
      editorView.renderReact(
        <ResultSchemaEditor schemaId={resultSchemaURLIdentifier} />,
        ASSAY_SCHEMA_SIDEBAR_SECTIONS[ASSAY_TYPES.ASSAY_RESULT]
      );
    });
  },

  runEditor(runId: string): void {
    DatadogUtil.startRUMView('run_editor');
    _editorForApiId(runId);
  },

  canvasEditor(canvasDefinitionId: string): void {
    if (!BenchlingConfig.ENABLE_CANVAS_CORE) {
      warnAndRedirectAfterInaccessiblePageOpen('Canvas is not available for your organization.');
      return;
    }
    DatadogUtil.startRUMView('canvas_editor');
    _editorForApiId(canvasDefinitionId);
  },

  canvasVersionEditor(canvasDefinitionId: string, canvasVersionId: string): void {
    if (!BenchlingConfig.ENABLE_CANVAS_CORE) {
      warnAndRedirectAfterInaccessiblePageOpen('Canvas is not available for your organization.');
      return;
    }
    DatadogUtil.startRUMView('canvas_editor');
    _editorForApiId(canvasDefinitionId, {versionApiId: canvasVersionId});
  },

  runSchemasCreate(): void {
    const features = [{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}];
    if (!areAnyFeaturesEnabled(features)) {
      warnAndRedirectAfterInaccessiblePageOpen(_getDisabledFeatureUserMessage(features)!);
      return;
    }

    DatadogUtil.startRUMView('run_schema_create');
    _editorBase(() => {
      editorView.renderReact(<RunSchemaEditor />, ASSAY_SCHEMA_SIDEBAR_SECTIONS[ASSAY_TYPES.ASSAY_RUN]);
    });
  },

  runSchema(ownerURL: string, runSchemaURLIdentifier: string): void {
    const features = [{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}];
    if (!areAnyFeaturesEnabled(features)) {
      warnAndRedirectAfterInaccessiblePageOpen(_getDisabledFeatureUserMessage(features)!);
      return;
    }

    DatadogUtil.startRUMView('run_schema_edit');
    _editorBase(() => {
      editorView.renderReact(
        <RunSchemaEditor schemaId={runSchemaURLIdentifier} />,
        ASSAY_SCHEMA_SIDEBAR_SECTIONS[ASSAY_TYPES.ASSAY_RUN]
      );
    });
  },

  templateEditor(userId: string, templateId: string, versionInformation: {versionId?: number} = {}): void {
    const {versionId} = versionInformation;
    DatadogUtil.startRUMView('template_editor');
    _editorBase(() => {
      // Immediately open the sidebar section for templates, otherwise it flashes the browser section.
      SidebarActions.openSectionForFileType(ModelTypeConstants.TEMPLATE);
      FileBrowserActions.openFile(templateId, {versionId});
    });
  },

  subtemplateEditor(userId: string, subtemplateId: string): void {
    DatadogUtil.startRUMView('subtemplate_editor');
    _editorBase(() => {
      // Immediately open the sidebar section for templates, otherwise it flashes the browser section.
      SidebarActions.openSectionForFileType(ModelTypeConstants.SUBTEMPLATE);
      FileBrowserActions.openFile(subtemplateId);
    });
  },

  templateCollections(userId: string): void {
    DatadogUtil.startRUMView('template_collections_list');
    _editorBase(() => {
      editorView.renderReact(
        <TemplateCollectionsPage />,
        SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTIONS
      );
    });
  },

  templateCollection(userId: string, unprefixedCollectionId: string): void {
    DatadogUtil.startRUMView('template_collection_list');
    const collectionId = APIUtil.prefixApiId(ModelTypeConstants.TEMPLATE_COLLECTION, unprefixedCollectionId);
    _editorBase(() => {
      TemplateActions.activateCollection(collectionId);
      editorView.renderReact(
        <TemplateCollectionPage collectionId={collectionId} />,
        SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
      );
    });
  },

  stageEntryEditor(stageEntryId: string): void {
    if (!BenchlingConfig.LEGACY_WORKFLOWS_ENABLE) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.WORKFLOWS, stageEntryId);
      return;
    }

    DatadogUtil.startRUMView('stage_entry_editor');
    _editorForApiId(stageEntryId);
  },

  folderItemEditor(fileUrlId: string, queryKey?: FileOpenParams): void {
    DatadogUtil.startFolderItemEditRUMView(fileUrlId);
    _editorForApiId(fileUrlId, queryKey);
  },

  sharedFolderItemEditor(): void {
    DatadogUtil.startRUMView('shared_folder_item_editor');
    const fileId = window.bootstrap && window.bootstrap.sharedFolderItemAPIId;
    let callback;
    if (window.bootstrap?.shareLinkMetadata) {
      callback = () =>
        LegacyModalContainer.open(
          SHARE_METADATA_MODAL_KEY,
          <ShareMetadataModal {...window.bootstrap!.shareLinkMetadata!} />
        );
    }
    _editorForApiId(fileId!, {}, callback);
  },

  registryGeneralSettings(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('registry_settings_general');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <RegistryGeneralSettingsPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_GENERAL
      );
    });
  },

  schemaInterfaces(): void {
    DatadogUtil.startRUMView('schemas_interfaces_list');
    _editorBase(() => {
      editorView.renderReact(<SchemaInterfacesPage />, SidebarConstants.Section.SCHEMA_INTERFACE_SETTINGS);
    });
  },

  schemaInterfaceCreate(): void {
    DatadogUtil.startRUMView('schema_interfaces_create');
    _editorBase(() => {
      editorView.renderReact(<SchemaInterfaceEditor />, SidebarConstants.Section.SCHEMA_INTERFACE_SETTINGS);
    });
  },

  schemaInterfaceSettings(interfaceId: string): void {
    DatadogUtil.startRUMView('schema_interface_settings');
    _editorBase(() => {
      editorView.renderReact(
        <SchemaInterfaceEditor interfaceId={interfaceId} />,
        SidebarConstants.Section.SCHEMA_INTERFACE_SETTINGS
      );
    });
  },

  tagSchemas(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('tag_schemas_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <RegistryTagSchemasSettingsPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_SCHEMAS
      );
    });
  },

  tagSchemaCreate(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('tag_schemas_create');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <TagSchemaCreateFlow ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.TAG_SCHEMA_SETTINGS
      );
    });
  },

  tagSchemaSettings(ownerUrlId: string, registryId: string, tagSchemaId: string, section?: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    const tab = section ? settingsTabForRouteSection(section) : null;
    DatadogUtil.startRUMView(tab != null ? `tag_schema_settings_${section}` : 'tag_schema_settings');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <TagSchemaSettingsPage
          ownerUrlId={ownerUrlId}
          registryId={registryId}
          tagSchemaId={tagSchemaId}
          activeTab={tab ?? undefined}
        />,
        SidebarConstants.Section.TAG_SCHEMA_SETTINGS
      );
    });
  },

  containerSchemas(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('container_schemas_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <ContainerSchemasPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_CONTAINER_SCHEMAS
      );
    });
  },

  _gridSchemas(
    ownerUrlId: string,
    registryId: string,
    gridSchemaClass: GridSchemaClass,
    settingsSection: NavConstants.SidepanelSection
  ): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView(`${gridSchemaClass}s_list`);
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <GridSchemasPage gridSchemaClass={gridSchemaClass} ownerUrlId={ownerUrlId} registryId={registryId} />,
        settingsSection
      );
    });
  },

  boxSchemas(ownerUrlId: string, registryId: string): void {
    EditorRoutes._gridSchemas(
      ownerUrlId,
      registryId,
      GridSchemaClass.BOX_SCHEMA,
      SidebarConstants.Section.REGISTRY_SETTINGS_BOX_SCHEMAS
    );
  },

  plateSchemas(ownerUrlId: string, registryId: string): void {
    EditorRoutes._gridSchemas(
      ownerUrlId,
      registryId,
      GridSchemaClass.PLATE_SCHEMA,
      SidebarConstants.Section.REGISTRY_SETTINGS_PLATE_SCHEMAS
    );
  },

  _gridSchemaSettings(
    ownerUrlId: string,
    registryId: string,
    gridSchemaId: string | null,
    gridSchemaClass: GridSchemaClass,
    settingsSection?: NavConstants.SidepanelSection
  ): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView(`${gridSchemaClass}_settings`);
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <GridSchemaSettingsPage
          gridSchemaClass={gridSchemaClass}
          gridSchemaId={gridSchemaId ?? undefined}
          ownerUrlId={ownerUrlId}
          registryId={registryId}
        />,
        settingsSection
      );
    });
  },

  boxSchemaSettings(ownerUrlId: string, registryId: string, boxSchemaId?: string): void {
    EditorRoutes._gridSchemaSettings(
      ownerUrlId,
      registryId,
      boxSchemaId ?? null,
      GridSchemaClass.BOX_SCHEMA,
      SidebarConstants.Section.REGISTRY_SETTINGS_BOX_SCHEMAS
    );
  },

  plateSchemaSettings(ownerUrlId: string, registryId: string, plateSchemaId?: string): void {
    EditorRoutes._gridSchemaSettings(
      ownerUrlId,
      registryId,
      plateSchemaId ?? null,
      GridSchemaClass.PLATE_SCHEMA,
      SidebarConstants.Section.REGISTRY_SETTINGS_PLATE_SCHEMAS
    );
  },

  locationSchemas(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('location_schemas_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <LocationSchemasPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LOCATION_SCHEMAS
      );
    });
  },

  locationSchemaSettings(
    ownerUrlId: string,
    registryId: string,
    locationSchemaId?: string,
    section?: string
  ): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    const tab = locationSettingsTabForRouteSection(section);
    DatadogUtil.startRUMView('location_schema_settings');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <LocationSchemaSettingsPage
          ownerUrlId={ownerUrlId}
          registryId={registryId}
          locationSchemaId={locationSchemaId}
          activeTab={tab}
        />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LOCATION_SCHEMAS
      );
    });
  },

  tenantLevelLabelPrintingSettings(): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    _editorBase(() => {
      editorView.renderReact(
        <LabelPrintingSettingsPage />,
        SidebarConstants.Section.TENANT_LEVEL_LABEL_PRINTING_SETTINGS
      );
    });
  },

  tenantLevelInventorySettings(): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    _editorBase(() => {
      editorView.renderReact(
        <InventorySettingsPage />,
        SidebarConstants.Section.TENANT_LEVEL_INVENTORY_SETTINGS
      );
    });
  },

  labelTemplates(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('label_templates_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <LabelPrintingSettingsContainer
          activeTab={ModelTypeConstants.LABEL_TEMPLATE}
          ownerUrlId={ownerUrlId}
          registryId={registryId}
        />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LABEL_PRINTING
      );
    });
  },

  labelTemplateSettings(ownerUrlId: string, registryId: string, labelTemplateId?: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('label_template_settings');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <LabelTemplateSettingsPage
          ownerUrlId={ownerUrlId}
          registryId={registryId}
          labelTemplateId={labelTemplateId}
        />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LABEL_PRINTING
      );
    });
  },

  printers(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('printers_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <LabelPrintingSettingsContainer
          activeTab={ModelTypeConstants.PRINTER}
          ownerUrlId={ownerUrlId}
          registryId={registryId}
        />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LABEL_PRINTING
      );
    });
  },

  printerSettings(ownerUrlId: string, registryId: string, printerId?: string): void {
    const featureStatus = getFeatureStatus({feature: InventoryFeature.INVENTORY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('printer_settings');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <PrinterSettingsPage ownerUrlId={ownerUrlId} registryId={registryId} printerId={printerId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_LABEL_PRINTING
      );
    });
  },

  schemaFieldSelectors(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('schema_field_selectors_list');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <RegistrySelectorsSettingsPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_SELECTORS
      );
    });
  },

  createSchemaFieldSelector(ownerUrlId: string, registryId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('schema_field_selector_create');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <SchemaFieldSelectorSettingsPage ownerUrlId={ownerUrlId} registryId={registryId} />,
        SidebarConstants.Section.REGISTRY_SETTINGS_SELECTORS
      );
    });
  },

  schemaFieldSelector(ownerUrlId: string, registryId: string, selectorId: string): void {
    const featureStatus = getFeatureStatus({feature: RegistryFeature.REGISTRY_ADMIN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('schema_field_selector_settings');
    _editorBase(() => {
      RegistryActions.activateRegistry(registryId);
      editorView.renderReact(
        <SchemaFieldSelectorSettingsPage
          ownerUrlId={ownerUrlId}
          registryId={registryId}
          selectorId={selectorId}
        />,
        SidebarConstants.Section.REGISTRY_SETTINGS_SELECTORS
      );
    });
  },

  eventSubscriptionsPage(): void {
    DatadogUtil.startRUMView('event_subscriptions_list');
    _editorBase(() => {
      editorView.renderReact(<EventSubscriptionsPage />, SidebarConstants.Section.EVENT_SUBSCRIPTIONS);
    });
  },

  developerBenchlingAppsPage(): void {
    DatadogUtil.startRUMView('benchling_apps_list');
    _editorBase(() => {
      editorView.renderReact(<BenchlingAppsPage />, SidebarConstants.Section.BENCHLING_APPS_SETTINGS);
    });
  },

  benchlingAppCanvasBuilderPage(): void {
    DatadogUtil.startRUMView('benchling_app_canvas_builder');
    _editorBase(() => {
      editorView.renderReact(<AppCanvasBuilder />, SidebarConstants.Section.BENCHLING_APP_CANVAS_BUILDER);
    });
  },

  benchlingAppEditor(benchlingAppId: string): void {
    DatadogUtil.startRUMView('benchling_app_editor');
    _editorForApiId(benchlingAppId);
  },

  installBenchlingAppPage(benchlingAppDefinitionId: string): void {
    DatadogUtil.startRUMView('install_benchling_app_view');
    _editorBase(() => {
      editorView.renderReact(<InstallBenchlingAppPage appDefinitionId={benchlingAppDefinitionId} />);
    });
  },

  developerBenchlingAppPage(benchlingAppId: string): void {
    DatadogUtil.startRUMView('benchling_app_view');
    _editorBase(() => {
      editorView.renderReact(
        <BenchlingAppPage id={benchlingAppId} />,
        SidebarConstants.Section.BENCHLING_APPS_SETTINGS
      );
    });
  },

  developerCreateBenchlingAppPage(): void {
    DatadogUtil.startRUMView('benchling_app_create');
    _editorBase(() => {
      editorView.renderReact(
        <CreateBenchlingAppPageLegacy />,
        SidebarConstants.Section.BENCHLING_APPS_SETTINGS
      );
    });
  },

  configMigrationExport(): void {
    DatadogUtil.startRUMView('config_migration');
    const ConfigExportPage = lazy(
      () => import(/* webpackChunkName: "config-migration" */ 'config-migration/export/ConfigExportPage')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <ConfigExportPage />
        </Suspense>,
        SidebarConstants.Section.CONFIG_MIGRATION
      );
    });
  },

  configMigrationImport(): void {
    DatadogUtil.startRUMView('config_migration');
    const ConfigImportPage = lazy(
      () => import(/* webpackChunkName: "config-migration" */ 'config-migration/import/ConfigImportPage')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <ConfigImportPage />
        </Suspense>,
        SidebarConstants.Section.CONFIG_MIGRATION
      );
    });
  },

  configMigrationPage(): void {
    DatadogUtil.startRUMView('config_migration');
    const ConfigMigrationPageWrapper = lazy(
      () => import(/* webpackChunkName: "config-migration" */ 'config-migration/ConfigMigrationPageWrapper')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <ConfigMigrationPageWrapper />
        </Suspense>,
        SidebarConstants.Section.CONFIG_MIGRATION
      );
    });
  },

  tenantComparisonPage(): void {
    DatadogUtil.startRUMView('tenant_comparison');
    const TenantComparisonPage = lazy(
      () => import(/* webpackChunkName: "tenant-comparison" */ 'tenant-comparison/TenantComparisonPage')
    );
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <TenantComparisonPage />
        </Suspense>,
        SidebarConstants.Section.TENANT_COMPARISON
      );
    });
  },

  containerEditor(containerId: string): void {
    DatadogUtil.startRUMView('container_editor');
    _editorForApiId(containerId);
  },

  gridEditor(unprefixedGridId: string): void {
    // TODO(T9772): Remove once grids have been migrated to boxes and plates
    DatadogUtil.startRUMView('grid_editor');
    const gridId = APIUtil.prefixApiId(ModelTypeConstants.GRID, unprefixedGridId);
    _editorForApiId(gridId);
  },

  boxEditor(boxId: string): void {
    DatadogUtil.startRUMView('box_editor');
    _editorForApiId(boxId);
  },

  plateEditor(plateId: string): void {
    DatadogUtil.startRUMView('plate_editor');
    _editorForApiId(plateId);
  },

  async locationExpandedSearch(locationId: string): Promise<void> {
    DatadogUtil.startRUMView('location_expanded_search');
    const storableSummaries = await StorableActions.bulkFetchSummaries([locationId]);
    assert(storableSummaries.length === 1, 'Single storable is expected.');
    const location = storableSummaries[0];
    _editorBase(() => {
      SidepanelActions.expand();
      require('FileStates').default.get().openLocationExpandedSearch(location);
    });
  },

  locationEditor(locationId: string): void {
    DatadogUtil.startRUMView('location_editor');
    _editorForApiId(locationId);
  },

  async storage(): Promise<void> {
    DatadogUtil.startRUMView('storage');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const storablesURLData = await parseStorablesURL(queryKey);
    if (storablesURLData) {
      getStore().dispatch(
        SidepanelReduxActions.openStorageSectionWithRegistry(
          NavConstants.SECTIONS.STORAGE,
          storablesURLData.searchParams,
          storablesURLData.registry.id
        )
      );
    }
    _editorBase(() => {
      SidepanelActions.expandSection(NavConstants.SECTIONS.STORAGE);
    });
  },

  workflowDashboard(): void {
    if (!BenchlingConfig.LEGACY_WORKFLOWS_ENABLE) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.WORKFLOWS);
      return;
    }

    DatadogUtil.startRUMView('legacy_workflows_dashboard');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseWorkflowsURL(
      queryKey as unknown as LegacyWorkflowQueryKey & SearchURLs.QueryKey
    );
    _sidepanelListing(NavConstants.Section.WORKFLOWS, searchParams);
  },

  workflowDashboardTemplates(): void {
    DatadogUtil.startRUMView('legacy_workflows_templates_list');
    _openWorkflowScreen(() => {
      WorkflowActions.openWorkflowDashboardTemplatesScreen();
    });
  },

  workflowDetails(workflowId: string): void {
    if (!BenchlingConfig.LEGACY_WORKFLOWS_ENABLE) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.WORKFLOWS, workflowId);
      return;
    }

    DatadogUtil.startRUMView('legacy_workflow_view');
    _openWorkflowScreen(() => {
      WorkflowActions.openWorkflowDetailsScreen(workflowId);
    });
  },

  workflowTemplateView(workflowTemplateId: string): void {
    if (!BenchlingConfig.LEGACY_WORKFLOWS_ENABLE) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.WORKFLOWS, workflowTemplateId);
      return;
    }

    DatadogUtil.startRUMView('legacy_workflow_template_view');
    _openWorkflowScreen(() => {
      WorkflowTemplateActions.openWorkflowTemplateViewScreen(workflowTemplateId);
    });
  },

  sidepanelListing(section: NavConstants.Section, defaultSearchParams: SearchParams): void {
    if (section === NavConstants.SECTIONS.REQUESTS && !BenchlingConfig.ENABLE_REQUESTS) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.REQUESTS);
      return;
    }
    if (section === NavConstants.SECTIONS.PROCESSES) {
      const featureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_DISCOVER});
      if (!featureStatus.enabled) {
        warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
        return;
      }
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_WORKFLOW_DASHBOARD);
    }
    if (
      section === NavConstants.SECTIONS.REQUESTS_V2_SUBMISSIONS ||
      section === NavConstants.SECTIONS.REQUESTS_V2_DEFINITIONS
    ) {
      const featureStatus = getFeatureStatus({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER});
      if (!featureStatus.enabled) {
        warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
        return;
      }
      analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.OPENED_REQUESTS_V2_DASHBOARD);
    }

    DatadogUtil.startRUMView(
      Object.values(NavConstants.Section).includes(section)
        ? `sidepanel_${section.toLowerCase()}`
        : DatadogUtil.unknownRoute
    );
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const querySearchParams = SearchURLs.searchParamsFromQueryKey(queryKey);
    const searchParams = {
      ...defaultSearchParams,
      ...querySearchParams,
      filters: FilterUtil.replaceByKey(defaultSearchParams.filters, ...(querySearchParams.filters || [])),
    };
    _sidepanelListing(section, searchParams);
  },

  processEditor(processId: string): void {
    const featureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_OPEN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('process_editor');
    _editorForApiId(processId);
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPENED_TASK_GROUP_DETAILS, {
      task_group_api_id: processId,
    });
  },

  processTaskEditor(processTaskId: string): void {
    const processOpenFeatureStatus = getFeatureStatus({feature: ProcessesFeature.PROCESSES_OPEN});
    if (!processOpenFeatureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(processOpenFeatureStatus.userMessage);
      return;
    }

    DatadogUtil.startRUMView('process_task_editor');
    _editorForApiId(processTaskId);
  },

  requestEditor(requestId: string): void {
    if (!BenchlingConfig.ENABLE_REQUESTS) {
      _redirectAfterInaccessibleLegacyGatedOpen(Capability.REQUESTS, requestId);
      return;
    }

    DatadogUtil.startRUMView('request_editor');
    _editorForApiId(requestId);
  },

  requestV2DefinitionPage(userId: string, requestV2DefinitionId: string): void {
    const featureStatus = getFeatureStatus({feature: V2RequestsFeature.V2_REQUESTS_OPEN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    DatadogUtil.startRUMView('request_v2_definition_editor');
    analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.OPENED_REQUEST_V2_DEFINITION_PAGE, {
      request_v2_definition_id: requestV2DefinitionId,
    });

    if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      _editorForApiId(requestV2DefinitionId);
    } else {
      _editorBase(() => {
        editorView.renderReact(
          <RequestV2DefinitionPage requestV2DefinitionId={requestV2DefinitionId} />,
          SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
        );
      });
    }
  },

  attachmentPreviewPage(
    owningEntityApiId: string,
    noteLinkedFileId: string,
    id: string,
    encodedName: string,
    isPreviewTransformed: string,
    previewType: EXTERNAL_FILE_TYPE,
    encodedUrl: string,
    encodedSourceUrl: string,
    encodedAvailablePreviewTypes: string
  ): void {
    DatadogUtil.startRUMView('attachment_preview_page');

    SidebarActions.close();
    _editorBase(() => {
      editorView.renderReact(
        <NoteLinkedFilePreviewPage
          encodedUrl={encodedUrl}
          encodedSourceUrl={encodedSourceUrl}
          id={id}
          previewType={previewType}
          owningEntityApiId={owningEntityApiId}
          noteLinkedFileId={noteLinkedFileId}
          encodedName={encodedName}
          isPreviewTransformed={isPreviewTransformed}
          encodedAvailablePreviewTypes={encodedAvailablePreviewTypes}
        />,
        SidebarConstants.Section.ATTACHMENT_PREVIEW
      );
    });
  },

  requestV2SubmissionEditor(userId: string, requestV2SubmissionId: string): void {
    const featureStatus = getFeatureStatus({feature: V2RequestsFeature.V2_REQUESTS_OPEN});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      return;
    }
    DatadogUtil.startRUMView('request_v2_submission_editor');
    analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.OPENED_REQUEST_V2_SUBMISSION_PAGE, {
      request_v2_submission_id: requestV2SubmissionId,
    });
    _editorForApiId(requestV2SubmissionId);
  },

  editor(): void {
    DatadogUtil.startRUMView('editor');
    _editorBase(() => {
      this._editor();
    });
  },

  _editor(): void {
    // exposed without wrapping _editorBase() call for easier testing.
    if (window.user != null) {
      // Preload the file browser with the home preview
      TFIActions.listAccounts();
    }
    _redirectForWorkspace();
  },

  bulkAntibody(): void {
    DatadogUtil.startRUMView('bulk_antibody');
    _editorBase(() => {
      FullScreenActions.showScreen(BULK_ANTIBODY_SCREEN);
    });
  },

  bulkAssembly(): void {
    DatadogUtil.startRUMView('bulk_assembly');
    _editorBase(() => {
      FullScreenActions.showScreen(BULK_ASSEMBLY_SCREEN);
    });
  },

  bulkTranslation(): void {
    DatadogUtil.startRUMView('bulk_translation');
    _editorBase(() => {
      FullScreenActions.showScreen(BULK_TRANSLATION_SCREEN);
    });
  },

  externalAnalyticsListing(): void {
    const featureStatus = getFeatureStatus({feature: InsightsFeature.INSIGHTS_DISCOVER});
    if (!featureStatus.enabled) {
      warnAndRedirectAfterInaccessiblePageOpen(featureStatus.userMessage);
      _editorBase();
      return;
    }

    DatadogUtil.startRUMView('sidepanel_analytics_list');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseAnalyticsURL(queryKey);
    _sidepanelListing(NavConstants.Section.ANALYTICS, searchParams);
  },

  externalAnalyticsDashboardDetails(analyticsDashboardId: string): void {
    DatadogUtil.startRUMView('analytics_dashboard');
    _editorForApiId(analyticsDashboardId);
  },

  analysisEditor(analysisId: string): void {
    DatadogUtil.startRUMView('analysis');
    _editorForApiId(analysisId);
  },

  analysisTemplateEditor(userId: string, analysisTemplateId: string): void {
    if (!isAnalysisTemplatesEnabled()) {
      warnAndRedirectAfterInaccessiblePageOpen('Automation templates are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('analysis_template');
    if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      _editorForApiId(analysisTemplateId);
    } else {
      _editorBase(() => {
        const AnalysisTemplatePage = lazy(
          () =>
            import(
              /* webpackChunkName: "analysis-template-page" */ 'analysis-tool/templates/AnalysisTemplatePage'
            )
        );
        const EditableAnalysisTemplatePage = lazy(
          () =>
            import(
              /* webpackChunkName: "editable-analysis-template-page" */ 'analysis-tool/templates/EditableAnalysisTemplatePage'
            )
        );
        editorView.renderReact(
          <Suspense fallback={<CenteredHorizontalThrobber />}>
            {BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES ? (
              <EditableAnalysisTemplatePage
                analysisTemplateId={analysisTemplateId}
                key={analysisTemplateId}
              />
            ) : (
              <AnalysisTemplatePage analysisTemplateId={analysisTemplateId} key={analysisTemplateId} />
            )}
          </Suspense>,
          SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
        );
      });
    }
  },

  plateDesignTemplateEditor(userId: string, plateDesignTemplateId: string): void {
    if (!BenchlingConfig.ENABLE_SCRATCH_PLATES) {
      warnAndRedirectAfterInaccessiblePageOpen('Plate design templates are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('plate_design_template');
    _editorForApiId(plateDesignTemplateId);
  },

  // TODO: Move studies() to client/studies/routes/StudyRoutes.tsx
  studies(): void {
    // TODO(BNCH-63553): Use capability management
    if (!BenchlingConfig.STUDIES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Studies are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('studies');
    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const searchParams = parseStudiesURL(queryKey);
    _sidepanelListing(NavConstants.Section.STUDIES, searchParams);
  },

  // TODO: (Copying jellyfish) Move to client/studies/study-schemas/routes/StudyRoutes.ts
  studySchemas(): void {
    // TODO(BNCH-63553): Use capability management
    if (!BenchlingConfig.STUDIES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Studies are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('study_schemas_list');
    _editorBase(() => {
      editorView.renderReact(<StudySchemasPage />, SidebarConstants.Section.STUDY_SCHEMAS);
    });
  },

  studySchemasCreate(): void {
    // TODO(BNCH-63553): Use capability management
    if (!BenchlingConfig.STUDIES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Studies are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('study_schemas_create');
    _editorBase(() => {
      editorView.renderReact(<StudySchemaEditor />, SidebarConstants.Section.STUDY_SCHEMAS);
    });
  },

  studySchema(schemaId: string): void {
    // TODO(BNCH-63553): Use capability management
    if (!BenchlingConfig.STUDIES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Studies are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('study_schema_view_or_edit');
    _editorBase(() => {
      editorView.renderReact(
        <StudySchemaEditor schemaId={schemaId} />,
        SidebarConstants.Section.STUDY_SCHEMAS
      );
    });
  },

  parameterLibraryListing(): void {
    if (!BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY) {
      warnAndRedirectAfterInaccessiblePageOpen('Parameter Library is not enabled.');
      return;
    }
    DatadogUtil.startRUMView('parameter_library_listing');
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <ParameterLibraryPage />
        </Suspense>,
        SidebarConstants.Section.PARAMETER_LIBRARY
      );
    });
  },

  procedureEditor(userId: string, procedureId: string): void {
    // We don't know the procedure type yet. We can be more forgiving here (checking all types) since there are
    // more restrictive checks inside the page views themselves
    if (
      Object.values(ProcedureType).every(
        (procedureType) => !isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.OPEN)
      )
    ) {
      warnAndRedirectAfterInaccessiblePageOpen('Procedures are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('procedure_view');

    if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      _editorForApiId(procedureId);
    } else {
      _editorBase(() => {
        const ProcedurePage = lazy(
          () => import(/* webpackChunkName: "procedure-page" */ 'procedures/ProcedurePage')
        );
        editorView.renderReact(
          <Suspense fallback={<CenteredHorizontalThrobber />}>
            <ProcedurePage procedureId={procedureId} key={procedureId} />
          </Suspense>,
          SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
        );
      });
    }
  },

  procedureFlowchartVersionViewer(userId: string, procedureId: string, flowchartVersionId: string): void {
    // [See comment in procedureEditor() above]
    if (
      Object.values(ProcedureType).every(
        (procedureType) => !isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.OPEN)
      )
    ) {
      warnAndRedirectAfterInaccessiblePageOpen('Procedures are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('procedure_view');

    if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      _editorForApiId(procedureId, {versionApiId: flowchartVersionId});
    } else {
      _editorBase(() => {
        const ProcedurePage = lazy(
          () => import(/* webpackChunkName: "procedure-page" */ 'procedures/ProcedurePage')
        );
        editorView.renderReact(
          <Suspense fallback={<CenteredHorizontalThrobber />}>
            <ProcedurePage
              procedureId={procedureId}
              key={procedureId}
              flowchartVersionId={flowchartVersionId}
            />
          </Suspense>,
          SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE_COLLECTION
        );
      });
    }
  },

  procedureRunEditor(procedureRunId: string): void {
    if (!BenchlingConfig.PROCEDURES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('First-class execution is not enabled for Procedures');
      return;
    }

    _editorForApiId(procedureRunId);
  },

  fileEditor(userId: string, fileId: string): void {
    DatadogUtil.startRUMView('files_view');
    _editorForApiId(fileId);
  },

  testDefinitions(): void {
    if (!BenchlingConfig.SAMPLE_TESTS_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Sample Tests are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('sample_tests_view');
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <TestDefinitionsPage />
        </Suspense>,
        SidebarConstants.Section.TEST_DEFINITIONS
      );
    });
  },

  testDefinitionCreate(): void {
    if (!BenchlingConfig.SAMPLE_TESTS_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Sample Tests are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('sample_tests_view');
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <TestDefinitionEditor />
        </Suspense>,
        SidebarConstants.Section.TEST_DEFINITIONS
      );
    });
  },

  testDefinition(testDefinitionId?: string): void {
    if (!BenchlingConfig.SAMPLE_TESTS_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Sample Tests are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('sample_tests_view');
    _editorBase(() => {
      editorView.renderReact(
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <TestDefinitionEditor testDefinitionId={testDefinitionId} />
        </Suspense>,
        SidebarConstants.Section.TEST_DEFINITIONS
      );
    });
  },

  sampleTypesListing(): void {
    if (!BenchlingConfig.SAMPLE_TYPES_ENABLE) {
      warnAndRedirectAfterInaccessiblePageOpen('Sample Types are not enabled.');
      return;
    }

    DatadogUtil.startRUMView('sample_types_view');
    _editorBase(() => {
      editorView.renderReact(<SampleTypesListing />, SidebarConstants.Section.SAMPLE_TYPES);
    });
  },

  libraryListing(): void {
    if (!BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
      warnAndRedirectAfterInaccessiblePageOpen('The Library is not enabled.');
      return;
    }

    const {queryKey} = benchlingUtil.parseUri(window.location.href);
    const {searchParams, templateCollectionId} = parseTemplateCollectionURL(queryKey);
    if (!templateCollectionId) {
      DatadogUtil.startRUMView('sidepanel_template_collections_list');
      _sidepanelListing(NavConstants.Section.TEMPLATE_COLLECTIONS, getDefaultLibrarySearchParams());
    } else {
      DatadogUtil.startRUMView('sidepanel_template_collection');
      _sidepanelListing(NavConstants.Section.TEMPLATE_COLLECTIONS, searchParams);
    }
  },
};

export default EditorRoutes;

if (module.hot) {
  module.hot.accept();
  // preserve editor view across webpack HMR
  module.hot.dispose((data) => {
    data.editorView = editorView;
  });
}
